import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonCheckbox, IonInput, IonItem, IonLabel, IonList } from '@ionic/angular/standalone';
import { takeUntil } from 'rxjs/operators';
import { DestroyService } from 'src/app/services/destroy.service';
import { Status } from '../status';

interface InputElementAccess { getInputElement: () => Promise<HTMLElement> }

@Component({
  selector: 'app-ssqw-checkbox-form',
  templateUrl: './ssqw-checkbox-form.component.html',
  styleUrls: ['./ssqw-checkbox-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  imports: [FormsModule, NgClass, ReactiveFormsModule, IonList, IonItem, IonLabel, IonCheckbox, IonInput]
})
export class SsqwCheckboxFormComponent implements OnInit {
  public checkboxOptions: { label: string; value: string; isChecked: boolean }[];
  @Input()
  public set options(value: string[]) {
    this.checkboxOptions = value.map((v, index) => ({
      label: v,
      value: index.toString(),
      isChecked: false
    }));
  }
  @Input() allowOther: boolean;
  @Input() initial: { checked: { label: string; value: string }[]; otherContent?: string };
  @Input() required: boolean;
  @Input() clearEvent: EventEmitter<void>;
  @Output() status = new EventEmitter<Status>();
  @Output() inputClicked = new EventEmitter<HTMLElement>();

  field = new FormControl('', [Validators.required]);

  constructor(private destroy$: DestroyService) {}
  ngOnInit(): void {
    this.clearEvent.pipe(takeUntil(this.destroy$)).subscribe(_ => this.field.setValue(''));

    if (this.allowOther) {
      this.checkboxOptions.push({ label: 'Other', value: 'other', isChecked: false });
    }

    this.field.statusChanges.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      this.status.emit(this.calculateStatus());
    });

    this.checkboxOptions.forEach((element) => {
      element.isChecked = !!this.initial?.checked.find(i => i.value === element.value);
    });
    this.field.addValidators(Validators.pattern(/^(?!\s+$).*/));
    this.field.setValue(this.initial?.otherContent);

    this.clearEvent.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.checkboxOptions.forEach((element) => {
        element.isChecked = false;
      });
      this.field.setValue('');
    });
  }

  onCheckBoxChanged(value: string, input: IonInput): void {
    this.status.emit(this.calculateStatus());
    if (value === 'other' && this.showOther) {
      (input as InputElementAccess).getInputElement().then((focusElement) => {
        setTimeout(() => focusElement.focus(), 0);
        this.inputClicked.emit(focusElement);
      });
    }
  }

  get showOther(): boolean {
    return !!this.checkboxOptions.find(o => o.value === 'other' && o.isChecked);
  }

  private calculateStatus(): Status {
    const selectedItems = this.checkboxOptions.filter(i => i.isChecked);
    if (selectedItems.length === 0) {
      return { valid: !this.required, errors: this.required ? { select: true } : null, value: null };
    }
    const valid = selectedItems.find(i => i.value === 'other') && !this.field.valid ? false : true;
    return {
      valid: valid,
      errors: valid ? null : this.field.errors,
      value: {
        checked: selectedItems.map(i => ({ label: i.label, value: i.value })),
        otherContent: this.field.valid ? this.field.value : null
      }
    };
  }

  get length(): number {
    return this.field.value?.length ?? 0;
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
    }
  }

  onClick(event: MouseEvent): void {
    this.inputClicked.emit(event.target as HTMLElement);
  }
}
